<script lang="ts">
  import { signInWithGoogle, ADMIN_EMAIL } from '../lib/firebase';

  let busy = $state(false);
  let err = $state('');
  async function go() {
    busy = true; err = '';
    try { await signInWithGoogle(); }
    catch (e) { err = (e as Error)?.message || String(e); }
    finally { busy = false; }
  }
</script>

<div class="login">
  <!-- Hero banner (your Shutterstock image), web-optimized. -->
  <div class="hero" style="background-image: url(/hero-login.jpg)" aria-hidden="true">
    <div class="scrim"></div>
  </div>

  <div class="panel">
    <div class="card">
      <div class="brandrow">
        <img class="lotus" src="/wellness-blue.svg" alt="" aria-hidden="true" />
        <span class="brand">empowered</span>
        <span class="beta">Beta</span>
      </div>
      <span class="eyebrow">Admin console</span>
      <h1>Access management</h1>
      <p class="sub">Invitations, allow / block, roles, and the physician directory — for the MPWRD&nbsp;/&nbsp;Danvantri health record.</p>

      <button class="g" onclick={go} disabled={busy}>
        <svg viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1c4.1-3.8 6.6-9.4 6.6-16.1z"/>
          <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8.1 41.1 15.5 46 24 46z"/>
          <path fill="#FBBC05" d="M11.8 28.2c-.4-1.3-.7-2.7-.7-4.2s.2-2.9.7-4.2v-5.7H4.5C3 17.1 2.2 20.4 2.2 24s.8 6.9 2.3 9.9l7.3-5.7z"/>
          <path fill="#EA4335" d="M24 10.8c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.1 29.9 2 24 2 15.5 2 8.1 6.9 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9 12.2-9z"/>
        </svg>
        {busy ? 'Signing in…' : 'Sign in with Google'}
      </button>
      {#if err}<p class="err">{err}</p>{/if}

      <p class="legal">Restricted to <strong>{ADMIN_EMAIL}</strong>. Every action is scoped by Firestore security rules.</p>
    </div>
  </div>
</div>

<style>
  /* Full-height split: left half is the hero (extends the whole page), right half
     holds the centered sign-in card. Stacks on narrow screens. */
  .login { height: 100dvh; display: grid; grid-template-columns: 1fr; grid-template-rows: 40vh 1fr; }
  @media (min-width: 900px) { .login { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr; } }

  /* The hero fills its grid cell — full page height on the left half (desktop). */
  .hero { position: relative; min-height: 0; background-size: cover; background-position: center 35%; }
  /* Light-theme scrim: veils the photo toward the page colour instead of toward
     black, so the hero blends into the light panel rather than sitting on it as
     a dark slab. No text is rendered over the hero, so this is purely tonal. */
  .hero .scrim {
    position: absolute; inset: 0;
    background:
      linear-gradient(180deg, rgba(245,247,250,0.10), rgba(245,247,250,0.45)),
      radial-gradient(90% 70% at 30% 30%, transparent, rgba(245,247,250,0.30));
  }
  @media (min-width: 900px) {
    /* Fade the hero into the sign-in panel on the right edge. */
    .hero .scrim { background: linear-gradient(90deg, rgba(245,247,250,0.10) 55%, var(--bg) 100%), linear-gradient(180deg, rgba(245,247,250,0.08), rgba(245,247,250,0.30)); }
  }

  .panel { display: grid; place-items: center; padding: var(--space-6); background: var(--bg); }
  .card { width: min(100%, 380px); display: flex; flex-direction: column; align-items: center; text-align: center; gap: var(--space-3); }

  .brandrow { display: inline-flex; align-items: center; gap: var(--space-2); }
  .lotus { width: 26px; height: 26px; object-fit: contain; }
  /* Same wordmark face as the main app (Audiowide), in the admin blue accent. */
  .brand { font-family: 'Audiowide', system-ui, sans-serif; font-weight: 400; letter-spacing: 0.02em; font-size: var(--fs-heading); color: var(--accent); }
  .beta { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent); background: var(--accent-soft); border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent); border-radius: var(--radius-pill); padding: 2px 6px; }
  .eyebrow { font-size: var(--fs-foot); font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; color: var(--text-3); margin-top: var(--space-2); }
  h1 { font-weight: var(--fw-heading); letter-spacing: -0.01em; }
  .sub { color: var(--text-2); font-size: var(--fs-body); max-width: 34ch; }

  /* Google's own guidance: white button, dark label, visible border. It used to
     be `background: var(--text); color: #14161A` — fine while --text was
     near-white, but on the light theme that is black-on-black. Both colours are
     now tokens, so the pair can never invert again. */
  .g { display: inline-flex; align-items: center; justify-content: center; gap: var(--space-3); margin-top: var(--space-3); padding: var(--space-3) var(--space-5); border-radius: var(--radius-pill); background: var(--surface); color: var(--text); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-sm); transition: transform var(--ease), box-shadow var(--ease); }
  .g:hover:not(:disabled) { transform: translateY(-1px); border-color: var(--accent); box-shadow: 0 8px 20px -12px rgba(15, 20, 26, 0.35); }
  .g:disabled { opacity: 0.6; cursor: default; }
  .g svg { width: 18px; height: 18px; }
  .err { color: var(--danger); font-size: var(--fs-xs); }
  .legal { color: var(--text-3); font-size: var(--fs-2xs); margin-top: var(--space-2); line-height: 1.5; }
  .legal strong { color: var(--text-2); }
</style>
